import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "api/user/UserProvider";
import { getAuthToken } from "api/authToken";
import { getDeclarationWindowQuery } from "api/datatrack/declaration/queries";
import {
  MANAGER_TOOLS_PATH,
  MANAGER_TOOLS_SECTIONS,
} from "constants/constants";
import {
  canSeeFafLatePopup,
  canUserPerformAction,
} from "access-roles/general-functions";
import { FAF_DECLARATION_ROLES } from "access-roles/roles";
import { getLateWeeks, isValidDeclarationWindow } from "utils/fafHelper";

export const FAF_LATE_POPUP_STORAGE_KEY = "faf_late_declaration_popup";
// Logins remembered per tab: the user's own and their latest mascarades.
const MAX_SHOWN_MARKS = 20;

// The end of a JWT (its signature) differs on every login: enough to tell sessions apart.
export const sessionMark = (token) => token.slice(-16);

// Storage can throw (private mode, blocked site data): treat it as empty.
const safeStorage = (action) => {
  try {
    return action();
  } catch {
    return null;
  }
};

// Logins already checked in this tab. A tab opened before the list format
// holds a single mark: it is read as a one-mark list.
const readShownMarks = () => {
  const stored = safeStorage(() =>
    sessionStorage.getItem(FAF_LATE_POPUP_STORAGE_KEY)
  );
  if (!stored) return [];
  const marks = safeStorage(() => JSON.parse(stored));
  return Array.isArray(marks) ? marks : [stored];
};

const rememberShownMark = (mark) => {
  const marks = [...readShownMarks().filter((m) => m !== mark), mark];
  safeStorage(() =>
    sessionStorage.setItem(
      FAF_LATE_POPUP_STORAGE_KEY,
      JSON.stringify(marks.slice(-MAX_SHOWN_MARKS))
    )
  );
};

const NOTHING_SHOWN = { mark: null, weeks: [] };

// Once per login, mascarade included. A login is marked in sessionStorage once
// the popup is closed (or when nothing is late): until then, a reload brings it
// back. Once marked, navigating, reloading or leaving a mascarade never does.
const useFafLateDeclarationPopup = () => {
  const { userRoleList, userScope, token } = useUserContext();
  const navigate = useNavigate();
  // The weeks found, with the login they belong to.
  const [shown, setShown] = useState(NOTHING_SHOWN);

  // In mascarade, the roles are the impersonated user's.
  const userRoles = userRoleList || [];
  // F&F declarants who can also read their declarations.
  const mustDeclare =
    canSeeFafLatePopup(userRoles) &&
    canUserPerformAction({
      userRoles,
      requiredRoles: FAF_DECLARATION_ROLES.ROLE_GET_DECLARATION,
    });
  // Requests carry the auth token. In mascarade it differs from the context
  // one, which is the original token again after a page reload.
  const currentMark = useCallback(
    () => (token ? sessionMark(safeStorage(getAuthToken) || token) : null),
    [token]
  );
  const mark = currentMark();

  useEffect(() => {
    if (!mustDeclare || !userScope || !mark) return;
    if (readShownMarks().includes(mark)) return;

    let cancelled = false;
    // Background check: a 401/403 must never log out or redirect the user.
    getDeclarationWindowQuery({
      handleForbiddenLocally: true,
      suppressUnauthorizedRedirect: true,
    })
      .then((declarationWindow) => {
        // Ignored if the login changed while waiting (a mascarade started or
        // ended). An unusable answer sets no mark: it is checked again only
        // if the scope or the login changes.
        if (cancelled || currentMark() !== mark) return;
        if (!isValidDeclarationWindow(declarationWindow)) return;
        const weeks = getLateWeeks(declarationWindow);
        // Nothing late: no need to ask the server again for this login. With
        // late weeks, the mark waits for the popup to be closed: a reload, by
        // mascaradeAs or by the user, may happen before the user sees it.
        if (weeks.length === 0) rememberShownMark(mark);
        setShown({ mark, weeks });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [mustDeclare, userScope, mark, currentMark]);

  // Weeks found for another login are never shown to the current one.
  const lateWeeks = shown.mark === mark ? shown.weeks : NOTHING_SHOWN.weeks;

  const close = useCallback(() => {
    if (shown.mark) rememberShownMark(shown.mark);
    setShown(NOTHING_SHOWN);
  }, [shown.mark]);

  const goToDeclaration = useCallback(() => {
    close();
    navigate(`/${userScope}/${MANAGER_TOOLS_PATH}/${MANAGER_TOOLS_SECTIONS.faf}`);
  }, [close, navigate, userScope]);

  return { open: lateWeeks.length > 0, lateWeeks, close, goToDeclaration };
};

export default useFafLateDeclarationPopup;
